import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import FileSystem from "examples/FileSystem";
import FileSingle from "examples/FileSingle";
import Footer from "examples/Footer";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";
import backgroundImage from "assets/images/bg-profile.jpeg";
import { UserContext } from "context";
import { useNotification } from "context/NotificationContext";

const apiUrl = process.env.REACT_APP_AUTH_API_URL;

function Overview() {
  const { user: loggedInUser } = useContext(UserContext);
  const { userId } = useParams();
  const [message, setMessage] = useState("");
  const [profile, setProfile] = useState({
    name: "",
    surname: "",
    email: "",
    job: "",
    VAT_number: "",
    ATECO: "",
    sdi_code: "",
    fiscal_code: "",
    phone_number: "",
    company_name: "",
    city: "",
    province: "",
    street: "",
    house_number: "",
    postal_code: "",
    end_documents_date_VA: "",
    end_documents_date_CA: "",
  });

  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const { showNotification } = useNotification();

  const fetchProfile = async () => {
    const idToFetch = userId || loggedInUser.id; // Use userId from params or loggedInUser id
    if (idToFetch) {
      try {
        const response = await fetch(`${apiUrl}/profile.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: idToFetch }),
        });
        const data = await response.json();
        if (response.ok) {
          setProfile(data);
        } else {
          console.error("Failed to fetch profile:", data.message);
        }
      } catch (error) {
        console.error("An error occurred while fetching profile:", error);
      }
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const handleSave = async (updatedInfo) => {
    try {
      const response = await fetch(`${apiUrl}/update_profile.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...updatedInfo, user_id: userId || loggedInUser.id }),
      });
      const data = await response.json();
      if (response.ok) {
        setProfile((prevProfile) => ({
          ...prevProfile,
          ...updatedInfo,
        }));
        fetchProfile();
        showNotification({
          color: "success",
          icon: <Icon>check_circle</Icon>,
          title: "Successo",
          dateTime: new Date().toLocaleString(),
          content: "Profilo aggiornato con successo.",
        });
      } else {
        showNotification({
          color: "error",
          icon: <Icon>error</Icon>,
          title: "Errore",
          dateTime: new Date().toLocaleString(),
          content: "Si è verificato un errore durante l'aggiornamento del profilo.",
        });
        console.error("Failed to update profile:", data.message);
      }
    } catch (error) {
      console.error("An error occurred while updating profile:", error);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await fetch(`${apiUrl}/delete_user.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId || loggedInUser.id }),
      });
      const data = await response.json();
      if (response.ok) {
        showNotification({
          color: "success",
          icon: <Icon>check_circle</Icon>,
          title: "Utente Eliminato",
          dateTime: new Date().toLocaleString(),
          content: "L'utente è stato eliminato con successo.",
        });
        navigate("/dashboard");
      } else {
        showNotification({
          color: "error",
          icon: <Icon>error</Icon>,
          title: "Eliminazione Fallita",
          dateTime: new Date().toLocaleString(),
          content: data.message || "Impossibile eliminare l'utente.",
        });
        console.error("Failed to delete user:", data.message);
      }
    } catch (error) {
      showNotification({
        color: "error",
        icon: <Icon>error</Icon>,
        title: "Errore",
        dateTime: new Date().toLocaleString(),
        content: "Si è verificato un errore durante l'eliminazione dell'utente.",
      });
      console.error("An error occurred while deleting user:", error);
    }
    setDeleteDialogOpen(false);
  };

  const handlePromoteToAdmin = async () => {
    try {
      const response = await fetch(`${apiUrl}/promote_to_admin.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId || loggedInUser.id }),
      });
      const data = await response.json();
      if (response.ok) {
        showNotification({
          color: "success",
          icon: <Icon>check_circle</Icon>,
          title: "Promozione a Admin",
          dateTime: new Date().toLocaleString(),
          content: "L'utente è stato promosso a Admin con successo.",
        });
      } else {
        showNotification({
          color: "error",
          icon: <Icon>error</Icon>,
          title: "Promozione Fallita",
          dateTime: new Date().toLocaleString(),
          content: data.message || "Impossibile promuovere l'utente.",
        });
        console.error("Failed to promote user:", data.message);
      }
    } catch (error) {
      showNotification({
        color: "error",
        icon: <Icon>error</Icon>,
        title: "Errore",
        dateTime: new Date().toLocaleString(),
        content: "Si è verificato un errore durante la promozione dell'utente.",
      });
      console.error("An error occurred while promoting user:", error);
    }
    setPromoteDialogOpen(false);
  };

  const handlePasswordReset = async () => {
    try {
      const response = await fetch(`${apiUrl}/password_reset_request.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: loggedInUser.id }),
      });

      const data = await response.json();
      navigate(data);
      if (!response.ok) {
        console.error("There was a problem with the fetch operation:", data.message);
        return;
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
  };

  const handleNewRequest = () => {
    navigate(`/app/eventRequest/${userId || loggedInUser.id}`);
  };

  const handleSubmitContact = async () => {
    try {
      const response = await fetch(`${apiUrl}/send_contact_email.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: profile.email,
          message: message,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showNotification({
          color: "success",
          icon: "check_circle",
          title: "Successo",
          dateTime: new Date().toLocaleString(),
          content: "Email inviata con successo.",
        });
        // Clear the form fields
        setMessage("");
      } else {
        showNotification({
          color: "error",
          icon: "error",
          title: "Errore",
          dateTime: new Date().toLocaleString(),
          content: data.message || "Impossibile inviare l'email.",
        });
      }
    } catch (error) {
      console.error("An error occurred while sending email:", error);
      showNotification({
        color: "error",
        icon: "error",
        title: "Errore",
        dateTime: new Date().toLocaleString(),
        content: "Si è verificato un errore durante l'invio dell'email.",
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox position="relative">
        <MDBox
          display="flex"
          alignItems="center"
          position="relative"
          borderRadius="xl"
          sx={{
            minHeight: { xs: "8.75rem", sm: "18.75rem" },
            backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
              `${linearGradient(
                rgba(gradients.info.main, 0.6),
                rgba(gradients.info.state, 0.6)
              )}, url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
          }}
        />
        <Card
          sx={{
            position: "relative",
            mt: { xs: -2, sm: -8 },
            mx: { xs: 0, sm: 3 },
            pt: 2,
            px: 2,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <MDBox height="100%" mt={0.5} ml={0.5} lineHeight={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  {profile.name} {profile.surname}
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  {profile.company_name}
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={5} mb={3}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6} xl={4}>
                <ProfileInfoCard
                  title="Informazioni del profilo"
                  info={{
                    nome: profile.name,
                    cognome: profile.surname,
                    email: profile.email,
                    mobile: profile.phone_number,
                    mansione: profile.job,
                  }}
                  action={{ route: "", tooltip: "Modificare" }}
                  shadow={false}
                  onSave={handleSave}
                />
              </Grid>
              <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
                <Divider orientation="vertical" sx={{ ml: -2, mr: 1 }} />
                <ProfileInfoCard
                  title="Informazioni aziendali"
                  info={{
                    "P IVA": profile.VAT_number,
                    "Codice Ateco": profile.ATECO,
                    "SDI Cod": profile.sdi_code,
                    "Codice Fiscale": profile.fiscal_code,
                    "Ragione Sociale": profile.company_name,
                  }}
                  action={{ route: "", tooltip: "Modificare" }}
                  shadow={false}
                  onSave={handleSave}
                />
              </Grid>
              <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
                <Divider orientation="vertical" sx={{ mx: 0 }} />
                <ProfileInfoCard
                  title="Address"
                  info={{
                    città: profile.city,
                    provincia: profile.province,
                    via: profile.street,
                    civico: profile.house_number,
                    cap: profile.postal_code,
                  }}
                  action={{ route: "", tooltip: "Modificare" }}
                  shadow={false}
                  onSave={handleSave}
                />
              </Grid>
              <Grid item xs={12} xl={4}></Grid>
            </Grid>
            {loggedInUser.isAdmin ? (
              <Grid container spacing={1}>
                <Grid item>
                  <MDButton
                    variant="contained"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <MDTypography
                      variant="h6"
                      color="white"
                      fontWeight="medium"
                      textTransform="capitalize"
                    >
                      Elimina utente
                    </MDTypography>
                  </MDButton>
                </Grid>
                {loggedInUser.isSuperAdmin && (
                  <Grid item>
                    <MDButton
                      variant="contained"
                      color="success"
                      onClick={() => setPromoteDialogOpen(true)}
                    >
                      <MDTypography
                        variant="h6"
                        color="white"
                        fontWeight="medium"
                        textTransform="capitalize"
                      >
                        Promuovi ad Admin
                      </MDTypography>
                    </MDButton>
                  </Grid>
                )}
                <Grid item>
                  <MDButton variant="contained" color="secondary" onClick={handleNewRequest}>
                    <MDTypography
                      variant="h6"
                      color="white"
                      fontWeight="medium"
                      textTransform="capitalize"
                    >
                      Nuova Richiesta
                    </MDTypography>
                  </MDButton>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid item>
                  <MDButton variant="contained" color="primary" onClick={handlePasswordReset}>
                    <MDTypography
                      variant="h6"
                      color="white"
                      fontWeight="medium"
                      textTransform="capitalize"
                    >
                      Password Reset
                    </MDTypography>
                  </MDButton>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <MDBox position="relative">
        <MDBox
          display="flex"
          alignItems="center"
          position="relative"
          minHeight="3rem"
          borderRadius="xl"
        />
        <Card
          sx={{
            position: "relative",
            mt: -3,
            mx: { xs: 0, sm: 3 },
            pt: 2,
            px: 2,
          }}
        >
          <Grid mt={2} container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox>
                <FileSingle
                  userId={userId || loggedInUser.id}
                  documentType="Visura Camerale"
                  subfolder={"Visura Camerale"}
                />
                {loggedInUser.isAdmin && (
                  <ProfileInfoCard
                    info={{
                      "Visura Camerale Expire Date": profile.end_documents_date_VC,
                    }}
                    action={{ route: "", tooltip: "Modificare" }}
                    shadow={false}
                    onSave={handleSave}
                    isAdmin={loggedInUser.isAdmin}
                    date={true}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox>
                <FileSingle
                  userId={userId || loggedInUser.id}
                  documentType="Carta di identità Amministratore"
                  subfolder={"Carta di identità Amministratore"}
                />
                {loggedInUser.isAdmin && (
                  <ProfileInfoCard
                    info={{
                      "Carta di identità Amministratore Expire Date": profile.end_documents_date_CA,
                    }}
                    action={{ route: "", tooltip: "Modificare" }}
                    shadow={false}
                    onSave={handleSave}
                    isAdmin={loggedInUser.isAdmin}
                    date={true}
                  />
                )}
              </MDBox>
            </Grid>
            {loggedInUser.isAdmin && (
              <Grid item xs={12} xl={8}>
                <MDBox height="100%" ml={0.5} mb={-2} lineHeight={1}>
                  <ProfileInfoCard
                    info={{
                      Commento: profile.comment,
                    }}
                    action={{ route: "", tooltip: "Modificare" }}
                    shadow={false}
                    onSave={handleSave}
                    isAdmin={loggedInUser.isAdmin}
                  />
                </MDBox>
              </Grid>
            )}
          </Grid>
          <MDBox mt={5} mb={3}>
            <FileSystem
              userId={userId || loggedInUser.id}
              subfolder={"subfolder"}
              isAdmin={loggedInUser.isAdmin}
            />
          </MDBox>
        </Card>
      </MDBox>
      {loggedInUser.isAdmin && (
        <MDBox position="relative" mb={5}>
          <MDBox
            display="flex"
            alignItems="center"
            position="relative"
            minHeight="3rem"
            borderRadius="xl"
          />
          <Card
            sx={{
              position: "relative",
              mt: -3,
              mx: { xs: 0, sm: 3 },
              py: 2,
              px: 2,
            }}
          >
            <Grid container spacing={3} alignItems="center" mb={1}>
              <Grid item>
                <MDBox height="100%" mt={0.5} ml={0.5} lineHeight={1}>
                  <MDTypography variant="h5" fontWeight="medium">
                    Contact
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
            <MDBox>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <MDInput
                    multiline
                    rows={7}
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{ minHeight: "6rem", resize: "none" }}
                  ></MDInput>
                </Grid>
                <Grid item>
                  <MDButton variant="contained" color="secondary" onClick={handleSubmitContact}>
                    <MDTypography
                      variant="h6"
                      color="white"
                      fontWeight="medium"
                      textTransform="capitalize"
                    >
                      Inviare
                    </MDTypography>
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
      )}
      <Footer />
      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare questo utente? Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialogOpen(false)} color="primary">
            Annulla
          </MDButton>
          <MDButton onClick={handleDeleteUser} color="error">
            Elimina
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* Promote to Admin Dialog */}
      <Dialog open={promoteDialogOpen} onClose={() => setPromoteDialogOpen(false)}>
        <DialogTitle>Conferma Promozione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler promuovere questo utente a Admin? Questa azione non può essere
            annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setPromoteDialogOpen(false)} color="primary">
            Annulla
          </MDButton>
          <MDButton onClick={handlePromoteToAdmin} color="primary">
            Promuovi
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Overview;

